'use client'

import Image from 'next/image'
import { IoChevronDown } from 'react-icons/io5'
import {
  DropdownGroupOption,
  SelectOption,
  CustomOptionProps,
  CustomSingleValueProps,
} from '@/types'

import Select, {
  components,
  ControlProps,
  PlaceholderProps,
  DropdownIndicatorProps,
  PropsValue,
} from 'react-select'
import React, { FocusEventHandler, useEffect, useState } from 'react'

interface SingleSelectProps {
  inputId: string
  dropdownLabel: string
  canSearchDropdown?: boolean
  options: { value: string; label: string }[] | DropdownGroupOption[]
  color?: 'grayborder' | 'failure'
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onSelectValue: (value: SelectOption) => void
  defaultValue?: PropsValue<SelectOption>
  menuPlacement?: 'top' | 'bottom' | 'auto'
}

const ControlComponent = (props: ControlProps<any, any, any>) => {
  return (
    <components.Control
      className={`!rounded-lg !cursor-pointer w-full h-[42px]`}
      {...props}
    />
  )
}

const Placeholder = (props: PlaceholderProps<any, any, any>) => {
  return <components.Placeholder className="text-sm text-left" {...props} />
}

const SingleValue = ({ children, ...props }: CustomSingleValueProps) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex gap-2 items-center">
        {props.data.icon && (
          <Image
            src={props.data.icon}
            className="w-5 h-4"
            alt=""
            width={0}
            height={0}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        )}
        <span>{children}</span>
      </div>
    </components.SingleValue>
  )
}

const IndicatorSeparator = () => {
  return <></>
}

const DropdownIndicator = (props: DropdownIndicatorProps<any, any, any>) => {
  return (
    <components.DropdownIndicator {...props}>
      <IoChevronDown className="text-gray-900 cursor-pointer" />
    </components.DropdownIndicator>
  )
}

const Option = (props: CustomOptionProps) => {
  return (
    <components.Option {...props}>
      <div className="flex gap-2 items-center">
        {props.data.icon && (
          <Image
            src={props.data?.icon}
            className="w-5 h-4"
            alt=""
            width={0}
            height={0}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        )}
        <span>{props.children}</span>
      </div>
    </components.Option>
  )
}

export default function SingleSelect({
  inputId,
  dropdownLabel,
  canSearchDropdown = false,
  options,
  color,
  defaultValue,
  onFocus,
  onBlur,
  onSelectValue,
  menuPlacement = 'auto',
}: SingleSelectProps) {
  return (
    <>
      <Select
        instanceId={inputId}
        isMulti={false}
        isSearchable={canSearchDropdown}
        options={options}
        placeholder={dropdownLabel}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e: any) => onSelectValue(e)}
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : null
        }
        menuPosition="fixed"
        // Without this the menu always opens downward. Combined with a fixed
        // menu in a body portal, one opened near the bottom of the page hangs
        // past the viewport and scrolling cannot bring it into view — the menu
        // is positioned against the viewport, not the document. It stranded
        // the last fields of the registration form (career level, nationality)
        // with their options unreachable. 'auto' flips the menu up when there
        // is no room below; MultiSelect already defaulted to this.
        menuPlacement={menuPlacement}
        components={{
          Control: ControlComponent,
          Placeholder,
          SingleValue,
          IndicatorSeparator,
          DropdownIndicator,
          Option,
        }}
        styles={{
          control: (base) => ({
            ...base,
            ...(color === 'failure' ? { 'border-color': 'red' } : {}),
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        defaultValue={defaultValue}
      />
    </>
  )
}
